/**
 * Connectif Shared Data Handler
 * Manages Connectif shared data retrieval and product rendering
 */

class ConnectifSharedDataHandler {
  constructor() {
    this.containers = new Map();
    this.init();
  }

  init() {
    // Listen for Connectif shared data received events
    document.addEventListener(
      'connectif.managed.shared_data_received',
      this.handleSharedDataReceived.bind(this)
    );

    // Initialize existing containers on page load
    if (window.connectif && window.connectif.managed) {
      this.initializeContainers();
    } else {
      // Wait for Connectif to be ready
      this.waitForConnectif();
    }
  }

  waitForConnectif() {
    const checkConnectif = setInterval(() => {
      if (window.connectif && window.connectif.managed) {
        clearInterval(checkConnectif);
        this.initializeContainers();
      }
    }, 100);

    // Stop checking after 10 seconds
    setTimeout(() => clearInterval(checkConnectif), 10000);
  }

  initializeContainers() {
    const containers = document.querySelectorAll('[data-connectif-identifier]');
    containers.forEach(container => {
      const identifier = container.getAttribute('data-connectif-identifier');
      if (identifier) {
        this.registerContainer(container, identifier);
        // Add delay similar to Magento2 implementation to allow Connectif to process data
        setTimeout(() => {
          this.getAndProcessSharedData(identifier);
        }, 100);
      }
    });
  }

  registerContainer(container, identifier) {
    this.containers.set(identifier, container);
  }

  handleSharedDataReceived(event) {
    if (event?.detail?.id) {
      this.getAndProcessSharedData(event.detail.id);
    }
  }

  getAndProcessSharedData(identifier, retryCount = 0) {
    const container = this.containers.get(identifier);
    
    if (!container) {
      return;
    }

    if (!window.connectif || !window.connectif.managed) {
      // console.warn('Connectif not available');
      // Hide container if Connectif is not available
      container.style.display = 'none';
      return;
    }

    const sharedData = window.connectif.managed.getSharedDataById(identifier);
    
    if (sharedData && sharedData.products && sharedData.products.length > 0) {
      this.processSharedData(identifier, sharedData);
    } else {
      // Retry up to 5 times with increasing delays (similar to Magento2 approach)
      if (retryCount < 5) {
        const delay = 200 * (retryCount + 1); // 200ms, 400ms, 600ms, 800ms, 1000ms
        // console.log(`No products found for identifier: ${identifier}, retrying in ${delay}ms (attempt ${retryCount + 1}/5)`);
        setTimeout(() => {
          this.getAndProcessSharedData(identifier, retryCount + 1);
        }, delay);
      } else {
        // Hide container if no products are available after all retries
        // console.warn(`No products found for identifier: ${identifier} after ${retryCount} retries`);
        container.style.display = 'none';
      }
    }
  }

  async processSharedData(identifier, sharedData) {
    const container = this.containers.get(identifier);
    
    if (!container) {
      // console.warn(`Container not found for identifier: ${identifier}`);
      return;
    }

    // console.log(`[Connectif] Processing shared data for: ${identifier}`, sharedData);

    // Show loading state
    container.classList.add('is-loading');

    try {
      // Map Connectif products to Shopify handles
      // console.log(`[Connectif] Mapping ${sharedData.products.length} products to Shopify handles...`);
      const productHandles = await this.mapProductsToShopify(sharedData.products);
      // console.log(`[Connectif] Mapped ${productHandles.length} product handles:`, productHandles);
      
      // Render products (this will hide container if no products can be rendered)
      // console.log(`[Connectif] Rendering products...`);
      await this.renderProducts(container, productHandles, sharedData);
      
      // Only update state if container is still visible (wasn't hidden by renderProducts)
      if (container.style.display !== 'none') {
        // Hide loading state
        container.classList.remove('is-loading');
        container.classList.add('is-loaded');
        // console.log(`[Connectif] Successfully loaded ${productHandles.length} products for ${identifier}`);

        // Dispatch custom event for tracking
        container.dispatchEvent(new CustomEvent('connectif:products-loaded', {
          detail: {
            identifier,
            productCount: productHandles.length
          }
        }));
      } else {
        // console.warn(`[Connectif] Container was hidden during rendering for ${identifier}`);
      }
    } catch (error) {
      // console.error('[Connectif] Error processing Connectif shared data:', error);
      container.classList.remove('is-loading');
      container.classList.add('has-error');
      // Hide container on error
      container.style.display = 'none';
    }
  }

  async mapProductsToShopify(connectifProducts) {
    // Map Connectif product IDs to Shopify product handles
    // This assumes you have a way to map external IDs to Shopify handles
    // You might need to adjust this based on your product ID mapping strategy
    
    const productHandles = [];
    
    for (const product of connectifProducts) {
      // Try to extract handle from productDetailUrl
      if (product.productDetailUrl) {
        // console.log(`[Connectif] Processing product URL: ${product.productDetailUrl}`);
        const url = new URL(product.productDetailUrl);
        const pathParts = url.pathname.split('/');
        const handle = pathParts[pathParts.length - 1].replace('.html', '');
        
        // console.log(`[Connectif] Extracted handle: "${handle}" from ${product.productDetailUrl}`);
        
        if (handle) {
          productHandles.push({
            handle: handle,
            connectifData: product
          });
        } else {
          // console.warn(`[Connectif] Could not extract handle from ${product.productDetailUrl}`);
        }
      } else {
        // console.warn(`[Connectif] Product missing productDetailUrl:`, product);
      }
    }
    
    return productHandles;
  }

  async renderProducts(container, productHandles, sharedData) {
    const gridContainer = container.querySelector('[data-connectif-products-grid]');
    
    if (!gridContainer) {
      // console.warn('[Connectif] Products grid container not found');
      container.style.display = 'none';
      return;
    }

    // Check if there are any product handles to render
    if (!productHandles || productHandles.length === 0) {
      // console.warn('[Connectif] No product handles to render');
      container.style.display = 'none';
      return;
    }

    // Get the section ID from container
    const sectionId = container.getAttribute('data-section-id');
    const enableSlider = container.getAttribute('data-enable-slider') === 'true';
    // console.log(`[Connectif] Section ID: ${sectionId}, Slider enabled: ${enableSlider}`);
    
    // Fetch and render each product
    // console.log(`[Connectif] Fetching ${productHandles.length} products...`);
    const productPromises = productHandles.map((item, index) => 
      this.fetchAndRenderProduct(item.handle, index, sectionId, item.connectifData)
    );

    const productCards = await Promise.all(productPromises);
    // console.log(`[Connectif] Fetched ${productCards.length} product cards`);
    
    // Filter out null/failed products
    const validProductCards = productCards.filter(card => card !== null);
    // console.log(`[Connectif] Valid product cards: ${validProductCards.length}`);
    
    // If no valid products were rendered, hide the container
    if (validProductCards.length === 0) {
      // console.warn('[Connectif] No valid products could be rendered');
      container.style.display = 'none';
      return;
    }
    
    // Clear existing content
    gridContainer.innerHTML = '';
    
    // Append product cards
    if (enableSlider) {
      // Wrap each card in swiper-slide
      validProductCards.forEach(card => {
        gridContainer.insertAdjacentHTML('beforeend', `<div class="swiper-slide">${card}</div>`);
      });
      
      // Initialize Swiper
      this.initializeSwiper(container);
    } else {
      // Append cards directly to grid
      validProductCards.forEach(card => {
        gridContainer.insertAdjacentHTML('beforeend', card);
      });
    }
    
    // console.log(`[Connectif] Successfully rendered ${validProductCards.length} products`);
  }

  initializeSwiper(container) {
    const swiperContainer = container.querySelector('[data-connectif-products-slider]');
    
    if (!swiperContainer || !window.Swiper) {
      // console.warn('[Connectif] Swiper not available or container not found');
      return;
    }

    const slidesPerViewMobile = parseInt(container.getAttribute('data-slides-per-view-mobile')) || 2;
    const slidesPerViewDesktop = parseInt(container.getAttribute('data-slides-per-view-desktop')) || 4;

    // console.log(`[Connectif] Initializing Swiper with mobile: ${slidesPerViewMobile}, desktop: ${slidesPerViewDesktop}`);

    const swiper = new Swiper(swiperContainer, {
      slidesPerView: slidesPerViewMobile,
      spaceBetween: 16,
      navigation: {
        nextEl: container.querySelector('.swiper-button-next'),
        prevEl: container.querySelector('.swiper-button-prev'),
      },
      breakpoints: {
        1024: {
          slidesPerView: slidesPerViewDesktop,
          spaceBetween: 20,
        }
      }
    });

    // console.log('[Connectif] Swiper initialized successfully');
  }

  async fetchAndRenderProduct(handle, position, sectionId, connectifData) {
    try {
      // Use Shopify Section Rendering API to fetch the product card
      // This fetches the connectif-product-card section with the product context
      const url = `/products/${handle}?sections=connectif-product-card`;
      // console.log(`[Connectif] Fetching product card via Section API: ${url}`);
      const response = await fetch(url);
      
      if (!response.ok) {
        // console.warn(`[Connectif] Failed to fetch product: ${handle} (Status: ${response.status})`);
        return null;
      }
      
      const data = await response.json();
      // console.log(`[Connectif] Received section data for ${handle}:`, data);
      
      // Extract the HTML from the section response
      const sectionHtml = data['connectif-product-card'];
      
      if (sectionHtml && sectionHtml.trim().length > 0) {
        // console.log(`[Connectif] Successfully fetched product card for ${handle}, HTML length: ${sectionHtml.length}`);
        
        // Parse HTML and extract only the product-card, avoiding the section wrapper with duplicate ID
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        
        // Find the product-card element (skip the shopify-section wrapper)
        const productCard = doc.querySelector('product-card, .product-card, [class*="product-card"]');
        
        if (productCard) {
          productCard.setAttribute('data-connectif-product-id', connectifData.productId);
          productCard.setAttribute('id', sectionId + '-' + connectifData.productId);
          productCard.setAttribute('data-connectif-priority', connectifData.priority || 0);
          return productCard.outerHTML;
        }
        
        // If no product-card found, try to extract content from the section wrapper
        const sectionWrapper = doc.querySelector('.shopify-section--connectif-product-card, [id*="shopify-section"]');
        if (sectionWrapper) {
          // Extract all children of the section wrapper, avoiding the wrapper itself
          const content = Array.from(sectionWrapper.children)
            .map(child => child.outerHTML)
            .join('');
          return content || null;
        }
        
        // console.warn(`[Connectif] No product-card element found for ${handle}`);
        return null;
      } else {
        // console.warn(`[Connectif] Empty or invalid HTML received for ${handle}`);
        return null;
      }
    } catch (error) {
      // console.error(`[Connectif] Error fetching product ${handle}:`, error);
      return null;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.connectifSharedDataHandler = new ConnectifSharedDataHandler();
  });
} else {
  window.connectifSharedDataHandler = new ConnectifSharedDataHandler();
}
